import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ThumbsUp, ThumbsDown, TrendingUp, TrendingDown, MessageSquare, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeedbackDashboard() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [actors, setActors] = useState([]);
  const [selectedActor, setSelectedActor] = useState("all");
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [convertCategory, setConvertCategory] = useState("feedback");

  useEffect(() => {
    fetchFeedback();
    fetchActors();
  }, [filterType, selectedActor]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("feedback_type", filterType);
      if (selectedActor !== "all") params.append("actor_id", selectedActor);

      const response = await axios.get(`${API}/admin/chat-feedback?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(response.data.feedback || []);
    } catch (error) {
      console.error("Failed to fetch feedback", error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const fetchActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActors(response.data);
    } catch (error) {
      console.error("Failed to fetch actors", error);
    }
  };

  const convertToCoaching = async () => {
    if (!selectedFeedback || !convertCategory) {
      toast.error("Please select a category");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/chat-feedback/${selectedFeedback.id}/convert-to-coaching?category=${convertCategory}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Feedback converted to coaching note");
      setSelectedFeedback(null);
      fetchFeedback(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert feedback");
    }
  };

  const stats = {
    total: feedback.length,
    positive: feedback.filter(f => f.feedback_type === "positive").length,
    negative: feedback.filter(f => f.feedback_type === "negative").length,
    converted: feedback.filter(f => f.converted_to_coaching).length
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Feedback Dashboard</h1>
        <p className="text-muted-foreground">Review user feedback and convert insights to coaching notes</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              {stats.total}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Positive Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2 text-green-600">
              <ThumbsUp className="w-5 h-5" />
              {stats.positive}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Negative Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2 text-red-600">
              <ThumbsDown className="w-5 h-5" />
              {stats.negative}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Converted to Coaching</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2 text-emerald-600">
              <CheckCircle className="w-5 h-5" />
              {stats.converted}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Feedback Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Feedback</SelectItem>
                  <SelectItem value="positive">Positive Only</SelectItem>
                  <SelectItem value="negative">Negative Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>AI Actor</Label>
              <Select value={selectedActor} onValueChange={setSelectedActor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actors</SelectItem>
                  {actors.map(actor => (
                    <SelectItem key={actor.id} value={actor.id}>
                      {actor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <Card>
        <CardHeader>
          <CardTitle>Feedback Entries ({feedback.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Loading feedback...</p>
          ) : feedback.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No feedback found</p>
          ) : (
            <div className="space-y-4">
              {feedback.map((item) => (
                <div
                  key={item.id}
                  className={`border rounded-lg p-4 ${
                    item.feedback_type === "positive" ? "border-green-200 bg-green-50/30" : "border-red-200 bg-red-50/30"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        {item.feedback_type === "positive" ? (
                          <ThumbsUp className="w-4 h-4 text-green-600" />
                        ) : (
                          <ThumbsDown className="w-4 h-4 text-red-600" />
                        )}
                        <Badge variant={item.feedback_type === "positive" ? "success" : "destructive"}>
                          {item.feedback_type}
                        </Badge>
                        <span className="text-sm font-semibold">{item.actor_name}</span>
                        {item.converted_to_coaching && (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Converted to Coaching
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          <strong>User:</strong> {item.user_name} ({item.user_email})
                        </p>
                        <p className="text-sm">
                          <strong>Question:</strong> {item.user_message}
                        </p>
                        <p className="text-sm">
                          <strong>AI Response:</strong> {item.ai_response.substring(0, 200)}
                          {item.ai_response.length > 200 && "..."}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div>
                      {!item.converted_to_coaching && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSelectedFeedback(item)}
                              className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
                            >
                              Convert to Coaching
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Convert Feedback to Coaching Note</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label>Select Category</Label>
                                <Input
                                  placeholder="e.g., pricing, technical, communication"
                                  value={convertCategory}
                                  onChange={(e) => setConvertCategory(e.target.value)}
                                />
                              </div>
                              <div className="bg-slate-50 p-3 rounded text-sm">
                                <p><strong>User Question:</strong> {item.user_message}</p>
                                <p className="mt-2"><strong>AI Response:</strong> {item.ai_response.substring(0, 150)}...</p>
                              </div>
                              <Button onClick={convertToCoaching} className="w-full bg-emerald-600 hover:bg-emerald-700">
                                Convert to Coaching Note
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
